import { useState } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '@/components/ui/tooltip';
import thorSquare from '@/assets/Thor_square.png';
import keystoneSquare from '@/assets/Keystone_square.jpeg';
import kzrvSquare from '@/assets/KZ-RV_square.svg';
import tiffinSquare from '@/assets/Tiffin_square.png';
import jaycoLogo from '/lovable-uploads/jayco-logo.svg';

const brandLogos = [
  { src: thorSquare, alt: 'Thor', key: 'Thor', bg: '#ffffff' },
  { src: jaycoLogo, alt: 'Jayco', key: 'Jayco', bg: '#ffffff' },
  { src: keystoneSquare, alt: 'Keystone', key: 'Keystone', bg: '#292929' },
  { src: kzrvSquare, alt: 'KZ-RV', key: 'KZ-RV', bg: '#ffffff' },
  { src: tiffinSquare, alt: 'Tiffin', key: 'Tiffin', bg: '#000000' },
];

export interface Profile {
  customerNumber?: string;
  name?: string;
  company?: string;
  userType?: string;
  userName?: string;
  id?: number;
  city?: string;
  state?: string;
  address?: string;
  address2?: string;
  zip?: string;
  country?: string;
  countryCode?: string;
  enabledBrands?: string[];
}

interface ProfileListProps {
  profiles: Profile[];
  onSelect: (profile: Profile, brandKey?: string) => void;
  currentCustomerNumber?: string;
  variant?: 'light' | 'dark';
  highlightCurrent?: boolean;
}

export const ProfileList = ({ profiles, onSelect, currentCustomerNumber, variant = 'light', highlightCurrent = false }: ProfileListProps) => {
  const [searchTerm, setSearchTerm] = useState('');

  const isDark = variant === 'dark';

  const switchable = highlightCurrent
    ? profiles.filter(p => p.userType === 'Profile')
    : profiles.filter(p => p.userType === 'Profile' && p.customerNumber !== currentCustomerNumber);

  if (switchable.length === 0) {
    return null;
  }

  const filtered = switchable.filter(p => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return [p.company, p.name, p.customerNumber, p.city, p.state]
      .some(field => field?.toLowerCase().includes(term));
  });

  const renderProfileButton = (profile: Profile, index: number) => {
    const displayName = profile.company || profile.name || `Profile ${profile.customerNumber || '(unknown)'}`;
    const addressLine = [profile.address, profile.address2].filter(Boolean).join(', ');
    const cityStateZip = [profile.city, [profile.state, profile.zip].filter(Boolean).join(' ')].filter(Boolean).join(', ');
    const isMissing = !profile.customerNumber;
    const isCurrent = highlightCurrent && profile.customerNumber === currentCustomerNumber;

    const buttonContent = (
      <div
        key={profile.customerNumber || index}
        className={`w-full py-3 px-4 rounded-lg border-2 transition-colors flex items-center gap-4 ${
          isCurrent
            ? 'border-primary bg-primary/10 ring-2 ring-primary/20'
            : isDark
              ? 'border-white/20 bg-white/10 text-white'
              : 'border-border bg-card text-foreground'
        } ${isMissing ? 'opacity-50' : ''}`}
      >
        <div className="flex-1 min-w-0">
          <div className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-foreground'}`}>
            {displayName} {profile.customerNumber ? `#${profile.customerNumber}` : ''}
          </div>
          {addressLine && (
            <div className={`text-xs ${isDark ? 'text-white/60' : 'text-muted-foreground'}`}>
              {addressLine}
            </div>
          )}
          {cityStateZip && (
            <div className={`text-xs ${isDark ? 'text-white/50' : 'text-muted-foreground'}`}>
              {cityStateZip}
            </div>
          )}
        </div>
        {!isMissing && (
          <div className="flex gap-2 shrink-0">
            {brandLogos.map((logo) => {
              const isEnabled = !profile.enabledBrands || profile.enabledBrands.includes(logo.key);
              return (
                <button
                  key={logo.key}
                  type="button"
                  disabled={!isEnabled}
                  onClick={(e) => { e.stopPropagation(); if (isEnabled) onSelect(profile, logo.key); }}
                  style={{ backgroundColor: logo.bg }}
                  className={`h-16 w-16 rounded-lg p-1 shadow-sm transition-transform ${
                    isEnabled ? 'hover:scale-110 cursor-pointer' : 'opacity-15 cursor-not-allowed grayscale'
                  }`}
                >
                  <img src={logo.src} alt={logo.alt} className="h-full w-full object-contain" />
                </button>
              );
            })}
          </div>
        )}
      </div>
    );

    if (isMissing) {
      return (
        <TooltipProvider key={profile.id || index}>
          <Tooltip>
            <TooltipTrigger asChild>
              {buttonContent}
            </TooltipTrigger>
            <TooltipContent>Missing Customer Number</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      );
    }

    return buttonContent;
  };

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 ${isDark ? 'text-white/50' : 'text-muted-foreground'}`} />
        <Input
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search by name, number, city or state..."
          className={`pl-10 ${isDark ? 'bg-transparent text-white border-white/20 placeholder:text-white/40 focus:border-white/40' : ''}`}
        />
      </div>
      <div className={`max-h-[500px] overflow-y-auto space-y-2 pr-1 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full ${isDark ? '[&::-webkit-scrollbar-track]:bg-white/5 [&::-webkit-scrollbar-thumb]:bg-white/20' : '[&::-webkit-scrollbar-thumb]:bg-muted-foreground/20'}`}>
        {filtered.length === 0 ? (
          <p className={`text-center py-4 text-sm ${isDark ? 'text-white/50' : 'text-muted-foreground'}`}>
            No profiles match your search.
          </p>
        ) : (
          filtered.map((profile, index) => renderProfileButton(profile, index))
        )}
      </div>
    </div>
  );
};
